import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';
import SparklineChart from './SparklineChart';

/**
 * AIInsightCard — Control Room style insight card.
 *
 * Props:
 * - category: "urgent" | "opportunity" | "win"
 * - icon: lucide-react component
 * - headline: string (1 line)
 * - metricValue: number — the € or % impact
 * - metricPrefix / metricSuffix: e.g. "-" / "€" / "%"
 * - why: string (1-2 sentences explaining)
 * - ctaLabel: string (action verb)
 * - onCta: () => void
 * - sparkline: number[]
 * - testId: string (e.g. "ai-insight-card-urgent-1")
 */
const CATEGORY_STYLES = {
    urgent: {
        accent: '#FF914D',
        glow: 'shadow-[0_0_40px_-12px_rgba(255,145,77,0.4)]',
        border: 'hover:border-[#FF914D]/40',
        textAccent: 'text-[#FF914D]',
        bgIcon: 'bg-[#FF914D]/10',
        chip: 'bg-[#FF914D]/15 text-[#FF914D] border-[#FF914D]/30',
        chipLabel: 'URGENT',
        ringPulse: true,
    },
    opportunity: {
        accent: '#A855F7',
        glow: 'shadow-[0_0_40px_-12px_rgba(168,85,247,0.35)]',
        border: 'hover:border-[#A855F7]/40',
        textAccent: 'text-[#C084FC]',
        bgIcon: 'bg-[#A855F7]/10',
        chip: 'bg-[#A855F7]/15 text-[#C084FC] border-[#A855F7]/30',
        chipLabel: 'OPPORTUNITÉ',
        ringPulse: false,
    },
    win: {
        accent: '#10B981',
        glow: 'shadow-[0_0_40px_-12px_rgba(16,185,129,0.35)]',
        border: 'hover:border-[#10B981]/40',
        textAccent: 'text-[#34D399]',
        bgIcon: 'bg-[#10B981]/10',
        chip: 'bg-[#10B981]/15 text-[#34D399] border-[#10B981]/30',
        chipLabel: 'WIN',
        ringPulse: false,
    },
};

export default function AIInsightCard({
    category = 'opportunity',
    icon: Icon,
    headline,
    metricValue = 0,
    metricPrefix = '',
    metricSuffix = ' €',
    why,
    ctaLabel = 'Traiter',
    onCta,
    sparkline = [],
    testId,
    delay = 0,
}) {
    const styles = CATEGORY_STYLES[category] || CATEGORY_STYLES.opportunity;

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3 }}
            className={`relative group flex flex-col gap-4 p-5 rounded-2xl
                bg-white/[0.03] backdrop-blur-xl border border-white/10
                ${styles.border} transition-all duration-300
                hover:bg-white/[0.05] ${styles.glow} hover:shadow-2xl`}
            data-testid={testId}
        >
            {/* Header: chip + icon */}
            <div className="flex items-start justify-between gap-3">
                <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider border ${styles.chip}`}
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                    {styles.chipLabel}
                </span>
                <div className={`relative w-9 h-9 rounded-xl flex items-center justify-center ${styles.bgIcon}`}>
                    {Icon && <Icon className="w-4 h-4" style={{ color: styles.accent }} />}
                    {styles.ringPulse && (
                        <span
                            className="absolute inset-0 rounded-xl ring-2 ring-[#FF914D]/50 animate-ping opacity-75"
                            style={{ animationDuration: '2s' }}
                        />
                    )}
                </div>
            </div>

            {/* Headline */}
            <h3
                className="text-base font-semibold text-white leading-snug tracking-tight"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
                {headline}
            </h3>

            {/* Metric — monospace, massive */}
            <div className="flex items-baseline gap-1">
                <span
                    className={`text-3xl md:text-4xl font-medium ${styles.textAccent} tabular-nums tracking-tight`}
                    style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                >
                    <AnimatedCounter
                        value={Math.abs(metricValue)}
                        prefix={metricPrefix}
                        suffix=""
                    />
                </span>
                <span
                    className={`text-base font-medium ${styles.textAccent} opacity-80`}
                    style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                >
                    {metricSuffix}
                </span>
            </div>

            {/* Why */}
            <p
                className="text-sm text-white/55 leading-relaxed"
                style={{ fontFamily: 'IBM Plex Sans, Manrope, sans-serif' }}
            >
                {why}
            </p>

            {/* Footer: sparkline + CTA */}
            <div className="flex items-center justify-between gap-3 mt-1 pt-3 border-t border-white/5">
                <SparklineChart data={sparkline} color={styles.accent} width={88} height={24} />
                <button
                    onClick={onCta}
                    className={`group/cta inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                        text-xs font-semibold text-white
                        bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20
                        transition-all`}
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    data-testid={`${testId}-cta`}
                >
                    {ctaLabel}
                    <ArrowRight className="w-3 h-3 transition-transform group-hover/cta:translate-x-0.5" />
                </button>
            </div>
        </motion.div>
    );
}
